import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, map, pluck } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { LoaderService } from '../loader.service';
import { LoginService } from '../login.service';
import { StorageService } from '../storage.service';
import { Pagination } from '../../models/pagination.model';
import {
  User,
  CustomerOrder,
  USER_ROLE_SUPERADMIN,
  USER_ROLE_ADMIN,
  USER_ROLE_RESTAURANT_MANAGER
} from '../../models/user.model';
import { CommonDataProvider, ICommonResponse } from './common.data-provider';
import { CompanyService } from '../company.service';

@Injectable()
export class UsersDataProvider extends CommonDataProvider<User> {
  private usersUrl = `${environment.apiUrl}/admin/users`;

  constructor(
    http: HttpClient,
    private companyService: CompanyService,
    private loginService: LoginService,
    private storageService: StorageService,
    loaderService: LoaderService
  ) {
    super(http, loaderService);
  }

  getUsers(q: string, page: number, tabType = 'ALL', per_page = 10): Observable<Pagination<User>> {
    const params = new HttpParams()
      .set('q', q)
      .set('company_id', this.companyService.COMPANY_ID.toString())
      .set('page', page.toString())
      .set('tabType', tabType)
      .set('per_page', per_page.toString());

    this.loaderService.show();
    return this.http.get<Pagination<User>>(this.usersUrl, { params }).pipe(
      map((d: Pagination<User>) => {
        this.loaderService.hide();
        return d;
      }),
      catchError(this.handleError)
    );
  }

  getCustomers(
    q: string,
    page: number,
    tabType = 'ALL',
    per_page = 10
  ): Observable<Pagination<User>> {
    const url = `${environment.apiUrl}/admin/companies/${this.companyService.COMPANY_ID}/customers`;
    const params = new HttpParams()
      .set('q', q)
      .set('page', page.toString())
      .set('tabType', tabType)
      .set('per_page', per_page.toString());

    this.loaderService.show();
    return this.http.get<Pagination<User>>(url, { params }).pipe(
      map((d: Pagination<User>) => {
        this.loaderService.hide();
        return d;
      }),
      catchError(this.handleError)
    );
  }

  getCustomerOrders(
    userId: number,
    page: number,
    per_page = 10
  ): Observable<Pagination<CustomerOrder>> {
    const url = `${environment.apiUrl}/admin/users/${userId}/orders`;
    const params = new HttpParams()
      .set('page', page.toString())
      .set('per_page', per_page.toString());

    this.loaderService.show();
    return this.http.get<Pagination<CustomerOrder>>(url, { params }).pipe(
      map((d: Pagination<CustomerOrder>) => {
        this.loaderService.hide();
        return d;
      }),
      catchError(this.handleError)
    );
  }

  getUserById(id: number): Observable<User> {
    return this.http.get<{ data: any }>(`${this.usersUrl}/${id}`).pipe(
      map(response => {
        const d = response.data;
        return {
          ...d,
          first_name: d.first_name || d.firstName,
          last_name: d.last_name || d.lastName
        } as User;
      }),
      catchError(this.handleError)
    );
  }

  deleteUser(id: number) {
    return this.http.delete(`${this.usersUrl}/${id}`).pipe(catchError(this.handleError));
  }

  toggleCorporateUserFlag(id: number): Observable<any> {
    return this.http
      .post(`${this.usersUrl}/${id}/corporate`, {})
      .pipe(catchError(this.handleError));
  }

  getUser(): Observable<User> {
    const user = this.storageService.getCurrentUser();
    if (user && user.id && user.permission) {
      return of(user);
    } else {
      return this.http
        .get<{ data: User }>(`${this.usersUrl}/me`)
        .pipe(pluck('data'), catchError(this.handleError));
    }
  }

  updateUser(user: Partial<User>) {
    return this.http.put<ICommonResponse>(`${this.usersUrl}/${user.id}`, user).pipe(
      map((response: ICommonResponse) => {
        this.storageService.setCurrentUser(response.data);
      }),
      catchError(this.handleError)
    );
  }

  updateCorporateData(user: Partial<User>) {
    return this.http
      .post(`${this.usersUrl}/${user.id}/corporate-data`, user)
      .pipe(catchError(this.handleError));
  }

  updatePassword(id: number, password: string) {
    return this.postAction(`${this.usersUrl}/${id}/password`, {
      password
    });
  }

  isUserAdmin() {
    const user = this.storageService.getCurrentUser()!;
    if (!user.anonym) {
      this.loginService.isUserLoggedInByCredentials = true;
    }
    if (this.loginService.isUserLoggedInByCredentials) {
      return of(user.admin);
    } else {
      return this.http.get<ICommonResponse>(`${this.usersUrl}/me`).pipe(
        map((response: ICommonResponse) => {
          this.storageService.setCurrentUser({ ...response.data, company: user.company });
          return response.data.admin;
        }),
        catchError(this.handleError)
      );
    }
  }

  isUserRestaurantManager() {
    const user = this.storageService.getCurrentUser()!;
    if (!user.anonym) {
      this.loginService.isUserLoggedInByCredentials = true;
    }
    if (this.loginService.isUserLoggedInByCredentials) {
      return of(user.restaurantManager);
    } else {
      return this.http.get<ICommonResponse>(`${this.usersUrl}/me`).pipe(
        map((response: ICommonResponse) => {
          this.storageService.setCurrentUser({ ...response.data, company: user.company });
          return response.data.restaurantManager;
        }),
        catchError(this.handleError)
      );
    }
  }

  isSuperManager() {
    const user = this.storageService.getCurrentUser()!;
    if (!user.anonym) {
      this.loginService.isUserLoggedInByCredentials = true;
    }
    if (this.loginService.isUserLoggedInByCredentials) {
      return of(user.super_manager);
    } else {
      return this.http.get<ICommonResponse>(`${this.usersUrl}/me`).pipe(
        map((response: ICommonResponse) => {
          this.storageService.setCurrentUser({ ...response.data, company: user.company });
          return response.data.super_manager;
        }),
        catchError(this.handleError)
      );
    }
  }

  userRoles$() {
    return combineLatest([
      this.isSuperManager().pipe(map(is => is && USER_ROLE_SUPERADMIN)),
      this.isUserAdmin().pipe(map(is => is && USER_ROLE_ADMIN)),
      this.isUserRestaurantManager().pipe(map(is => is && USER_ROLE_RESTAURANT_MANAGER))
    ]).pipe(map(roles => roles.filter(a => !!a)));
  }
}
